import { useEffect } from "preact/hooks";

/**
 * While a full-screen surface (sheet / full mode) is open: track the visual viewport height (mobile keyboard),
 * expose it as --wk-vvh on the root, lock host page scrolling, and close on Escape.
 */
export function useFullscreenViewport(active: boolean, root: HTMLElement | null, onEscape: () => void): void {
  useEffect(() => {
    if (!active || !root) return;
    const vv = window.visualViewport;
    const update = () => root.style.setProperty("--wk-vvh", `${Math.round(vv?.height ?? window.innerHeight)}px`);
    update();
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    const html = document.documentElement;
    const prev = { overflow: html.style.overflow, overscroll: html.style.overscrollBehavior };
    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onEscape();
    window.addEventListener("keydown", onKey);
    return () => {
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("keydown", onKey);
      root.style.removeProperty("--wk-vvh");
      html.style.overflow = prev.overflow;
      html.style.overscrollBehavior = prev.overscroll;
    };
  }, [active, root, onEscape]);
}

/** True when the popup should open as a full-screen sheet (phones, short viewports). Mirrors the CSS media query. */
export function useIsSheet(): boolean {
  return matchMedia("(max-width: 40em), (max-height: 34em)").matches;
}
